import { ApprovingInvoiceReportsSearchQuery } from '@/context/services/approving_invoice_reports';
import {
  SearchApprovingInvoiceReportsQueryMfFile,
  SearchApprovingInvoiceReportsQueryStatus,
} from 'ap-openapi';
import { toISODateString } from 'date-util';
import { Dayjs } from 'dayjs';
import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';

// Form data with form-friendly date format
export type SearchFormData = {
  page?: number;
  // Date fields in form-friendly format
  created_date?: (string | Dayjs)[] | undefined;
  submitted_date?: (string | Dayjs)[] | undefined;
  due_date?: (string | Dayjs)[] | undefined;
  approved_date?: (string | Dayjs)[] | undefined;
  book_date?: (string | Dayjs)[] | undefined;

  // Direct API fields
  number?: string;
  query?: string;
  status?: SearchApprovingInvoiceReportsQueryStatus | '';
  ap_payee_id?: string;
  mf_file?: SearchApprovingInvoiceReportsQueryMfFile | '';
  without_agent?: boolean;
  office_member_ids?: string[];
  dept_ids?: string[];
  report_dept_ids?: string[];
  total_amount_from?: string;
  total_amount_to?: string;
  sort_fields?: string; // Comma-separated sort fields
  sort_orders?: string; // Comma-separated sort orders
};

interface UseSearchFormProps {
  defaultValues?: SearchFormData;
  form: UseFormReturn<SearchFormData>;
}

// Default form values
export const getDefaultFormValues = (): SearchFormData => ({
  // Date fields in form-friendly format
  created_date: ['', ''],
  submitted_date: ['', ''],
  due_date: ['', ''],
  approved_date: ['', ''],
  book_date: ['', ''],

  // Direct API fields
  number: '',
  query: '',
  status: '',
  ap_payee_id: '',
  mf_file: '',
  without_agent: false,
  office_member_ids: [],
  dept_ids: [],
  report_dept_ids: [],
  total_amount_from: '',
  total_amount_to: '',
  sort_fields: undefined,
  sort_orders: undefined,
  page: 1,
});

// Convert form data to API query format
export const convertFormDataToApiQuery = (
  formData: SearchFormData
): ApprovingInvoiceReportsSearchQuery => {
  const query: ApprovingInvoiceReportsSearchQuery = {};

  if (formData.number) query.number = formData.number;
  if (formData.query) query.query = formData.query;
  if (formData.status) query.status = formData.status || '';
  if (formData.ap_payee_id)
    query.ap_payee_id = formData.ap_payee_id || undefined;
  if (formData.mf_file) query.mf_file = formData.mf_file;
  if (formData.without_agent) query.without_agent = formData.without_agent;
  if (formData.office_member_ids?.length)
    query.office_member_ids = formData.office_member_ids;
  if (formData.dept_ids?.length) query.dept_ids = formData.dept_ids;
  if (formData.report_dept_ids?.length)
    query.report_dept_ids = formData.report_dept_ids;
  if (formData.total_amount_from)
    query.total_amount_from = formData.total_amount_from || undefined;
  if (formData.total_amount_to)
    query.total_amount_to = formData.total_amount_to || undefined;

  if (formData.page) query.page = formData.page;

  // Convert date ranges
  if (formData.created_date?.length === 2) {
    if (formData.created_date[0])
      query.created_at_from =
        toISODateString(formData.created_date[0]) || undefined;
    if (formData.created_date[1])
      query.created_at_to =
        toISODateString(formData.created_date[1]) || undefined;
  }
  if (formData.submitted_date?.length === 2) {
    if (formData.submitted_date[0])
      query.submitted_at_from =
        toISODateString(formData.submitted_date[0]) || undefined;
    if (formData.submitted_date[1])
      query.submitted_at_to =
        toISODateString(formData.submitted_date[1]) || undefined;
  }
  if (formData.due_date?.length === 2) {
    if (formData.due_date[0])
      query.due_date_from = toISODateString(formData.due_date[0]) || undefined;
    if (formData.due_date[1])
      query.due_date_to = toISODateString(formData.due_date[1]) || undefined;
  }
  if (formData.approved_date?.length === 2) {
    if (formData.approved_date[0])
      query.approved_at_from =
        toISODateString(formData.approved_date[0]) || undefined;
    if (formData.approved_date[1])
      query.approved_at_to =
        toISODateString(formData.approved_date[1]) || undefined;
  }
  if (formData.book_date?.length === 2) {
    if (formData.book_date[0])
      query.book_date_from =
        toISODateString(formData.book_date[0]) || undefined;
    if (formData.book_date[1])
      query.book_date_to = toISODateString(formData.book_date[1]) || undefined;
  }
  if (formData?.sort_fields && formData?.sort_orders) {
    query.sort_fields = formData?.sort_fields;
    query.sort_orders = formData?.sort_orders;
  }

  return query;
};

export const useSearchForm = ({
  form,
}: Omit<UseSearchFormProps, 'defaultValues'> & {}) => {
  const handleReset = useCallback(() => {
    form.reset(getDefaultFormValues());
    form.clearErrors();
  }, [form]);

  return {
    handleReset,
  };
};
